import React, { useState } from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import TextFieldWithLabel from '@ui-parts/TextFieldWithLabel';
import SaveButton from '@ui-elements/SaveButton';
import BasicModal from '@ui-parts/BasicModal';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Box, FormControl, Typography, RadioGroup } from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Radio from '@ui-elements/Radio';
import Checkbox from '@ui-elements/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputValidationErrorBox from '@ui-parts/InputValidationErrorBox';
import { emptyValueFilter } from '@functional';
import { FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { ANTISOCIAL_CHECK } from '~/constants';
import { callThunk, updateAntiSocialCheck, fetchSavedDeliverySettings } from '~/api';
import { antisocialCheckNotesValidationRule } from '~/constants/validationRules';
import { GMOASK_STATUS } from '~/constants/api';

const CustomModalHeader = styled(Box)`
  padding: 30px 51px 24px 51px;
`;

const StyledIcon = styled(WarningRoundedIcon)`
  font-size: 2.4rem;
  margin-right: 6px;
`;

const Caution = styled.div`
  display: flex;
  align-items: center;
  font-size: ${FONT_SIZES.body3};
  color: ${PALETTE.error.main};
  margin-top: 12px;
`;

const Note = styled.div`
  font-size: ${FONT_SIZES.body3};
  border: 1px solid #d8e3ed;
  padding: 16px;
  margin-top: 18px;
  max-height: 213px;
  overflow: auto;
`;

const NoteDesc = styled.p`
  white-space: pre-wrap;
  word-wrap: break-word;
  margin-top: 10px;
  line-height: 1.5;
`;

const ModalFooterInner = styled.div`
  padding-bottom: 28px;
  div[color='error'] {
    margin: -5px 0 15px;
  }
`;
const ConfirmCheck = styled.div`
  margin-bottom: 10px;
  .MuiFormControlLabel-root span {
    font-size: 1.8rem;
  }
`;

const StyledBox = styled(Box)`
  padding: 0 16px;
`;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const RadioBox = styled.div`
  width: 300px;
  height: 75px;
  margin-top: 10px;
  padding: 15px 20px;
  border: solid ${PALETTE.border.frame} 1px;
  border-radius: 15px;
  background: ${PALETTE.white};
  text-align: center;
  .MuiRadio-root {
    margin: 0 7px 0 0;
  }
`;

const TextFieldWithLabelWrapper = styled.div`
  margin-top: 18px;
  color: ${PALETTE.error.main};
  .MuiFormLabel-root {
    color: ${PALETTE.text.main};
  }
  .MuiBox-root {
    padding: 0;
  }
  .MuiOutlinedInput-root {
    width: 638px;
    textarea {
      // MUIのTextFieldのinline styleを上書き
      height: 128px !important;
    }
    fieldset {
      border: 1px solid #d8e3ed;
      border-radius: 0;
    }
  }
`;

function AntisocialCheckModal({ open, handleClose }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { survey } = useSelector((state) => state);
  const judgement = useSelector((state) => state.judgement?.userJudgement);
  const [confirmCheck, setConfirmCheck] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    getValues,
    setValue,
    reset,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      notes: '',
      result: null,
    },
  });

  const onCloseModal = () => {
    clearErrors();
    reset();
    setConfirmCheck(false);
    handleClose();
  };

  const onClickSave = async () => {
    const params = {
      surveyId: survey.id,
      result: getValues().result === ANTISOCIAL_CHECK.APPROVED.toString(), // to Boolean
      notes: getValues().notes.trim(),
    };

    const { payload } = await dispatch(
      callThunk({
        action: updateAntiSocialCheck,
        payload: emptyValueFilter(params),
      })
    );

    // 審査フロー枠の表示更新のため、fetchSavedDeliverySettingsを呼ぶ
    /* istanbul ignore next OK以外はエラー処理に移る為、帰ってこないが念の為 */
    if (payload.status === GMOASK_STATUS.OK) {
      await dispatch(
        callThunk({
          action: fetchSavedDeliverySettings,
          payload: { surveyId: survey.id },
        })
      );

      onCloseModal();
    }
  };

  return (
    <BasicModal
      open={open}
      width="764px"
      dataTestId="AntisocialCheckModal"
      titleProps={{
        customModalTitle: [
          <CustomModalHeader key="targetSamplesDomProps">
            <Typography variant="title1" fontWeight="bold" data-testid="uiParts-CustomModalTitle">
              {t('adminSurvey.JudgementBox.antisocialCheck.modal.head')}
            </Typography>
            <Caution variant="title1">
              <StyledIcon />
              {t('adminSurvey.JudgementBox.antisocialCheck.modal.headNote')}
            </Caution>

            {judgement?.antisocialCheckApproval?.notes && (
              <Note>
                <Typography variant="title3" component="p" fontWeight="bold">
                  {t('adminSurvey.JudgementBox.antisocialCheck.modal.notesTitle')}
                </Typography>
                <NoteDesc>{judgement?.antisocialCheckApproval?.notes}</NoteDesc>
              </Note>
            )}
          </CustomModalHeader>,
        ],
      }}
      handleClose={() => onCloseModal()}
      buttons={[
        <ModalFooterInner key="ModalFooterInner">
          <ConfirmCheck>
            <Checkbox
              name="confirmCheck"
              label={t('adminSurvey.JudgementBox.antisocialCheck.modal.confirmCheckLabel')}
              fontSize={38}
              handleChange={(e) => {
                setConfirmCheck(e.target.checked);
              }}
              checked={confirmCheck}
              inputProps={{
                ...register('confirmCheck', {
                  validate: {
                    message: () => (confirmCheck ? null : t('validation.requiredCheck')),
                  },
                }),
              }}
              dataTestId="AntisocialCheckModal-confirmCheck"
            />
          </ConfirmCheck>
          {!!errors.confirmCheck && (
            <InputValidationErrorBox
              message={errors?.confirmCheck?.message}
              dataTestId="confirmCheck"
              position="center"
            />
          )}
          <SaveButton
            handleClick={handleSubmit(onClickSave)}
            data-testid="AntisocialCheckModal-SaveButton"
            sx={{ width: 148 }}
          />
        </ModalFooterInner>,
      ]}
    >
      <StyledBox>
        <FormControl component="fieldset" error fullWidth>
          <Controller
            rules={{ required: true }}
            name="result"
            control={control}
            render={({ field }) => (
              <StyledRadioGroup
                {...field}
                name={field.name}
                onChange={(e) => {
                  const { value } = e.target;
                  field.onChange(value);
                }}
                value={field.value}
              >
                <RadioBox>
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        data-testid="antisocialCheck-radio-approved"
                        value={ANTISOCIAL_CHECK.APPROVED.toString()} // stringにしないとwarningになる
                      />
                    }
                    label={
                      <Typography>
                        {t('adminSurvey.JudgementBox.antisocialCheck.modal.approved')}
                      </Typography>
                    }
                  />
                </RadioBox>
                <RadioBox>
                  <FormControlLabel
                    control={
                      <Radio
                        color="primary"
                        data-testid="antisocialCheck-radio-rejected"
                        value={ANTISOCIAL_CHECK.REJECTED.toString()}
                      />
                    }
                    label={
                      <Typography>
                        {t('adminSurvey.JudgementBox.antisocialCheck.modal.rejected')}
                      </Typography>
                    }
                  />
                </RadioBox>
              </StyledRadioGroup>
            )}
          />
        </FormControl>
        {!!errors.result && (
          <InputValidationErrorBox
            message={t('validation.requiredCheck')}
            dataTestId="result"
            position="left"
          />
        )}
        <TextFieldWithLabelWrapper>
          <Controller
            rules={antisocialCheckNotesValidationRule}
            name="notes"
            control={control}
            render={({ field }) => (
              <TextFieldWithLabel
                label={t('adminSurvey.JudgementBox.antisocialCheck.modal.textFiledLabel')}
                name={field.name}
                dataTestId="AntisocialCheckModal-Textfield-notes"
                width="622px"
                labelColor="secondary"
                value={field.value}
                error={errors.notes}
                handleChange={(e) => {
                  setValue('notes', e.target.value);
                }}
                handleBlur={(e) => {
                  setValue('notes', e.target.value.trim());
                }}
              />
            )}
          />
          {!!errors.notes && (
            <InputValidationErrorBox
              message={errors?.notes?.message}
              dataTestId="notes"
              position="left"
            />
          )}
        </TextFieldWithLabelWrapper>
      </StyledBox>
    </BasicModal>
  );
}

AntisocialCheckModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default AntisocialCheckModal;
